import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI } from '../../styles/theme';
import KycProgressBar from '../KycProgressBar';
import API from '../../api';

// ── Validazione Codice Fiscale Italiano ───────────────────────────────────────
const CF_REGEX = /^[A-Z]{6}[0-9]{2}[ABCDEHLMPRST][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;

const CF_ODD = {
  '0':1,'1':0,'2':5,'3':7,'4':9,'5':13,'6':15,'7':17,'8':19,'9':21,
  'A':1,'B':0,'C':5,'D':7,'E':9,'F':13,'G':15,'H':17,'I':19,'J':21,
  'K':2,'L':4,'M':18,'N':20,'O':11,'P':3,'Q':6,'R':8,'S':12,'T':14,
  'U':16,'V':10,'W':22,'X':25,'Y':24,'Z':23,
};

const validateCodiceFiscale = (cf) => {
  const s = cf.toUpperCase().trim();
  if (s.length !== 16) return false;
  if (!CF_REGEX.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const c = s[i];
    if (i % 2 === 0) {
      // posizioni dispari (0-indexed pari)
      sum += CF_ODD[c];
    } else {
      // posizioni pari (0-indexed dispari): valore naturale
      sum += c >= '0' && c <= '9' ? c.charCodeAt(0) - 48 : c.charCodeAt(0) - 65;
    }
  }
  return String.fromCharCode(65 + (sum % 26)) === s[15];
};

const PersonalData = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '',
    birthDate: '', birthCity: '', birthProvince: '', taxCode: '',
    residenceAddress: '', residenceCity: '', residenceProvince: '',
    residenceZip: '', citizenship: 'IT',
  });
  const [error, setError] = useState('');
  const [taxCodeError, setTaxCodeError] = useState('');
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const { t } = useTranslation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'taxCode') {
      const upper = value.toUpperCase();
      if (upper.length === 16) {
        setTaxCodeError(validateCodiceFiscale(upper) ? '' : t('kyc.personal_data.error_tax_code_invalid'));
      } else {
        setTaxCodeError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateCodiceFiscale(formData.taxCode)) {
      setTaxCodeError(t('kyc.personal_data.error_tax_code_invalid'));
      return;
    }
    try {
      await API.post('/kyc/personal-data', { user_id: userId, ...formData });
      navigate('/kyc/document-upload');
    } catch (err) {
      setError(t('kyc.personal_data.error_save'));
    }
  };

  const inputStyle = { width: '100%', padding: UI.spacing.md, backgroundColor: SEMANTIC.backgroundCard, color: SEMANTIC.textPrimary, border: '1px solid rgba(255,255,255,0.15)', borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.md };
  const labelStyle = { color: SEMANTIC.textPrimary, display: 'block', marginBottom: UI.spacing.sm, fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold };

  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundApp, minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: UI.spacing.lg }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: UI.spacing.xl }}>
          <img src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`} alt="Kuesto Logo" style={{ width: '120px' }} />
        </div>

        {/* Progress Bar */}
        <KycProgressBar currentStep={3} />

        <div style={{ backgroundColor: SEMANTIC.backgroundCard, borderRadius: UI.borderRadius.md, padding: UI.spacing.xl, boxShadow: UI.shadow.card }}>
          <h2 style={{ color: SEMANTIC.actionBuy, marginBottom: UI.spacing.md, fontSize: UI.fontSize.title, fontWeight: UI.fontWeight.bold, textAlign: 'center' }}>
            {t('kyc.personal_data.title')}
          </h2>
          <p style={{ color: SEMANTIC.textSecondary, marginBottom: UI.spacing.xl, textAlign: 'center', fontSize: UI.fontSize.md }}>
            {t('kyc.personal_data.description')}
          </p>
          {error && (
            <div style={{ backgroundColor: 'rgba(255, 77, 77, 0.1)', border: `2px solid ${SEMANTIC.actionSell}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.md, marginBottom: UI.spacing.lg, color: SEMANTIC.actionSell, textAlign: 'center' }}>{error}</div>
          )}
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: UI.spacing.md, marginBottom: UI.spacing.lg }}>
              <div style={{ flex: 1 }}><label htmlFor="firstName" style={labelStyle}>{t('kyc.personal_data.first_name')}</label><input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder={t('kyc.personal_data.first_name_placeholder')} required style={inputStyle} /></div>
              <div style={{ flex: 1 }}><label htmlFor="lastName" style={labelStyle}>{t('kyc.personal_data.last_name')}</label><input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder={t('kyc.personal_data.last_name_placeholder')} required style={inputStyle} /></div>
            </div>
            <div style={{ marginBottom: UI.spacing.lg }}><label htmlFor="birthDate" style={labelStyle}>{t('kyc.personal_data.birth_date')}</label><input type="date" id="birthDate" name="birthDate" value={formData.birthDate} onChange={handleChange} required style={inputStyle} /></div>
            <div style={{ marginBottom: UI.spacing.lg }}><label htmlFor="birthCity" style={labelStyle}>{t('kyc.personal_data.birth_city')}</label><input type="text" id="birthCity" name="birthCity" value={formData.birthCity} onChange={handleChange} placeholder={t('kyc.personal_data.birth_city_placeholder')} required style={inputStyle} /></div>
            <div style={{ marginBottom: UI.spacing.lg }}><label htmlFor="birthProvince" style={labelStyle}>{t('kyc.personal_data.birth_province')}</label><input type="text" id="birthProvince" name="birthProvince" value={formData.birthProvince} onChange={handleChange} placeholder={t('kyc.personal_data.birth_province_placeholder')} maxLength="2" required style={inputStyle} /></div>
            <div style={{ marginBottom: UI.spacing.lg }}>
              <label htmlFor="taxCode" style={labelStyle}>{t('kyc.personal_data.tax_code')}</label>
              <input
                type="text" id="taxCode" name="taxCode"
                value={formData.taxCode} onChange={handleChange}
                placeholder={t('kyc.personal_data.tax_code_placeholder')}
                maxLength="16" required
                style={{ ...inputStyle, textTransform: 'uppercase', border: `1px solid ${taxCodeError ? SEMANTIC.actionSell : formData.taxCode.length === 16 && !taxCodeError ? SEMANTIC.actionBuy : 'rgba(255,255,255,0.15)'}` }}
              />
              {taxCodeError && (
                <p style={{ color: SEMANTIC.actionSell, fontSize: UI.fontSize.sm, marginTop: '6px', marginBottom: 0 }}>
                  <i className="bi bi-exclamation-circle" style={{ marginRight: '4px' }} />{taxCodeError}
                </p>
              )}
              {formData.taxCode.length === 16 && !taxCodeError && (
                <p style={{ color: SEMANTIC.actionBuy, fontSize: UI.fontSize.sm, marginTop: '6px', marginBottom: 0 }}>
                  <i className="bi bi-check-circle" style={{ marginRight: '4px' }} />{t('kyc.personal_data.tax_code_valid')}
                </p>
              )}
            </div>
            <h3 style={{ color: SEMANTIC.textPrimary, marginTop: UI.spacing.xl, marginBottom: UI.spacing.lg, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold }}>{t('kyc.personal_data.residence_title')}</h3>
            <div style={{ marginBottom: UI.spacing.lg }}><label htmlFor="residenceAddress" style={labelStyle}>{t('kyc.personal_data.address')}</label><input type="text" id="residenceAddress" name="residenceAddress" value={formData.residenceAddress} onChange={handleChange} placeholder={t('kyc.personal_data.address_placeholder')} required style={inputStyle} /></div>
            <div style={{ marginBottom: UI.spacing.lg }}><label htmlFor="residenceCity" style={labelStyle}>{t('kyc.personal_data.city')}</label><input type="text" id="residenceCity" name="residenceCity" value={formData.residenceCity} onChange={handleChange} placeholder={t('kyc.personal_data.city_placeholder')} required style={inputStyle} /></div>
            <div style={{ display: 'flex', gap: UI.spacing.md, marginBottom: UI.spacing.lg }}>
              <div style={{ flex: 1 }}><label htmlFor="residenceProvince" style={labelStyle}>{t('kyc.personal_data.province')}</label><input type="text" id="residenceProvince" name="residenceProvince" value={formData.residenceProvince} onChange={handleChange} placeholder={t('kyc.personal_data.province_placeholder')} maxLength="2" required style={inputStyle} /></div>
              <div style={{ flex: 1 }}><label htmlFor="residenceZip" style={labelStyle}>{t('kyc.personal_data.zip')}</label><input type="text" id="residenceZip" name="residenceZip" value={formData.residenceZip} onChange={handleChange} placeholder={t('kyc.personal_data.zip_placeholder')} maxLength="5" required style={inputStyle} /></div>
            </div>
            <div style={{ marginBottom: UI.spacing.xl }}><label htmlFor="citizenship" style={labelStyle}>{t('kyc.personal_data.citizenship')}</label><select id="citizenship" name="citizenship" value={formData.citizenship} onChange={handleChange} required style={inputStyle}>
              <option value="IT">{t('kyc.personal_data.citizenship_IT')}</option>
              <option value="US">{t('kyc.personal_data.citizenship_US')}</option>
              <option value="GB">{t('kyc.personal_data.citizenship_GB')}</option>
              <option value="DE">{t('kyc.personal_data.citizenship_DE')}</option>
              <option value="FR">{t('kyc.personal_data.citizenship_FR')}</option>
              <option value="ES">{t('kyc.personal_data.citizenship_ES')}</option>
              <option value="OTHER">{t('kyc.personal_data.citizenship_OTHER')}</option>
            </select></div>
            <button type="submit" style={{ width: '100%', padding: UI.spacing.md, backgroundColor: SEMANTIC.ctaPrimary, color: SEMANTIC.ctaPrimaryText, border: 'none', borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold, cursor: 'pointer' }}>{t('common.continue')}</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PersonalData;
